import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  IconButton,
  useScrollTrigger,
  Slide,
  Typography,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import ContrastIcon from '@mui/icons-material/Contrast';
import { useGlobalState } from '../../global/context/global-context';
import { useAccessibility } from '../../global/context/accessibility-context';
import AppMenu from './app-menu';
import SearchBar from './search-bar';
import { getFormattedEntityName } from '../../../utils/entity-formatter';
import { useNavigate } from '../../../hooks/useNavigate';
import { useParams } from 'react-router-dom';

interface HideOnScrollProps {
  children: React.ReactElement;
}

function HideOnScroll({ children }: HideOnScrollProps) {
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export default function AppHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { applicationData } = useGlobalState();
  const { increaseFontSize, decreaseFontSize, toggleHighContrast, highContrast } = useAccessibility();
  const navigate = useNavigate();
  const params = useParams<{ state?: string; city?: string }>();
  const cnpj = applicationData?.entity?.document || '';

  const handleDrawerToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const getHref = (path: string) => {
    if (params.state && params.city) {
      return `/${params.state}/${params.city}${path}`;
    }
    return path;
  };

  return (
    <React.Fragment>
      {/* Top Bar - Acessibilidade */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: 'primary.dark',
          py: 0.5,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            variant="dense"
            sx={{
              minHeight: 40,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {/* Links da esquerda */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, md: 2 },
                alignItems: 'center',
                flex: 1,
              }}
            >
              <Box
                component="a"
                href={getHref('/acessibilidade')}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/acessibilidade');
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <AccessibilityNewIcon fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Acessibilidade
                </Box>
              </Box>

              {/* Accessibility Controls */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  borderLeft: 1,
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  pl: { xs: 1, md: 2 },
                  ml: { xs: 1, md: 2 },
                }}
              >
                <Tooltip title="Diminuir fonte" arrow>
                  <IconButton
                    size="small"
                    onClick={decreaseFontSize}
                    sx={{
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    aria-label="diminuir tamanho da fonte"
                  >
                    <TextDecreaseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Aumentar fonte" arrow>
                  <IconButton
                    size="small"
                    onClick={increaseFontSize}
                    sx={{
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    aria-label="aumentar tamanho da fonte"
                  >
                    <TextIncreaseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={highContrast ? "Desativar alto contraste" : "Ativar alto contraste"} arrow>
                  <IconButton
                    size="small"
                    onClick={toggleHighContrast}
                    sx={{
                      color: 'white',
                      backgroundColor: highContrast ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                    aria-label="alternar alto contraste"
                  >
                    <ContrastIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Links da direita */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, md: 3 },
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Box
                component="a"
                href={`http://docgedsistemas.com.br/ouvidoria?codEntidade=${cnpj}&empresa=3851`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <RecordVoiceOverIcon fontSize="small" />
                Ouvidoria
              </Box>
              <Box
                component="a"
                href={`http://docgedsistemas.com.br/sep?codEntidade=${cnpj}&empresa=3851`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <CloudUploadIcon fontSize="small" />
                Envio de Publicações
              </Box>
              <Box
                component="a"
                href="http://scp.docgedsistemas.com.br/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <AssignmentIcon fontSize="small" />
                SCP
              </Box>
              <Box
                component="a"
                href={`http://docgedsistemas.com.br/AdministratorPortal?codEntidade=${cnpj}&empresa=3851`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                <AdminPanelSettingsIcon fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Área Administrativa
                </Box>
              </Box>
              <SearchBar />

            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Header - Logo e Nome */}
      <Box
        sx={{
          backgroundColor: 'background.paper',
          py: { xs: 3, md: 4 },
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              component="img"
              src={
                applicationData?.entity?.brandLogo
                  ? `data:image/png;base64,${applicationData?.entity?.brandLogo}`
                  : applicationData?.entity?.stateBrandLogo
              }
              alt="Logo"
              sx={{
                height: { xs: 60, md: 80 },
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 700,
                textAlign: 'center',
                color: 'text.primary',
              }}
            >
              {getFormattedEntityName(applicationData?.entity)}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Navigation Menu - Sticky */}
      <HideOnScroll>
        <AppBar
          position="sticky"
          elevation={2}
          sx={{
            backgroundColor: 'background.paper',
            color: 'text.primary',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar
              sx={{
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
              }}
            >
              <IconButton
                color="inherit"
                aria-label="abrir menu"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>

              <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1, justifyContent: 'center' }}>
                <AppMenu isMobile={false} onClose={() => { }} />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>

      {/* Mobile Drawer */}
      <AppMenu isMobile={true} open={mobileMenuOpen} onClose={handleDrawerToggle} />
    </React.Fragment>
  );
}
